import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { Activity, ActivityCreate, ActivityUpdate } from '../types'

// Activities query/mutation hooks. Follows the same pattern as useEvents:
// list query keyed on eventId; mutations write back into the cache so the
// UI reacts without a follow-up GET.

export function useActivities(eventId: number) {
  return useQuery({
    queryKey: ['activities', eventId],
    queryFn: () => ApiClient.activities(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

// Append the new activity into the cached list. POST returns the canonical
// wire shape so this is a drop-in append; we don't bother resorting because
// the server orders by (sort_order, id) and the new row's id is always the
// largest, which is what we want for "appears last in the list".
export function useCreateActivity(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ActivityCreate) => ApiClient.createActivity(eventId, body),
    onSuccess: (created: Activity) => {
      qc.setQueryData<Activity[]>(['activities', eventId], (old) =>
        old ? [...old, created] : [created]
      )
    }
  })
}

// Replace the matching activity in the cached list. The update body is
// passed alongside the id so the mutation only needs one TanStack-mutation
// shape (object) rather than positional args.
export function useUpdateActivity(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ActivityUpdate }) =>
      ApiClient.updateActivity(eventId, id, body),
    onSuccess: (updated: Activity) => {
      qc.setQueryData<Activity[]>(['activities', eventId], (old) =>
        old ? old.map((a) => a.id === updated.id ? updated : a) : [updated]
      )
    }
  })
}

// Remove the activity from the cached list. The mutationFn returns the
// deleted id (the API returns 204 with no body, so we have to plumb the id
// through ourselves) so onSuccess knows what to filter.
export function useDeleteActivity(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      ApiClient.deleteActivity(eventId, id).then(() => id),
    onSuccess: (deletedId: number) => {
      qc.setQueryData<Activity[]>(['activities', eventId], (old) =>
        old ? old.filter((a) => a.id !== deletedId) : []
      )
    }
  })
}
